import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle2, XCircle, Send, Unplug, ArrowRight, AlertOctagon, AlertTriangle, Archive, Activity, ArrowUpCircle, CalendarClock, CalendarX, DollarSign, Gauge, History, Info, KeyRound, Layers, List, Pause, Play, Plug, PlusCircle, RefreshCw, RotateCcw, Search, Settings, Settings2, Shield, Sliders, Trash2, TrendingUp, UserMinus, Users } from 'lucide-react';
import { apiClient } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import clickhouseIcon from '../assets/icons/clickhouse.svg';

const POLL_INTERVAL_OPTIONS = [
  { value: 60, label: '60 seconds' },
  { value: 300, label: '5 minutes' },
  { value: 900, label: '15 minutes' },
  { value: 1800, label: '30 minutes' },
];

const formatPollInterval = (seconds?: number): string => {
  if (!seconds) return '5 minutes';
  if (seconds % 60 === 0) {
    const mins = seconds / 60;
    return mins === 1 ? '1 minute' : `${mins} minutes`;
  }
  return `${seconds} second${seconds !== 1 ? 's' : ''}`;
};

const ClickHouseIntegration: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [exampleService, setExampleService] = useState('');
  const [exampleChannel, setExampleChannel] = useState('');
  const [lastPollAt, setLastPollAt] = useState<string | null>(null);
  const [resourcesTracked, setResourcesTracked] = useState(0);
  const [pollInterval, setPollInterval] = useState(300);
  const [organization, setOrganization] = useState('');

  const [keyId, setKeyId] = useState('');
  const [keySecret, setKeySecret] = useState('');
  const [connectOrg, setConnectOrg] = useState('');
  const [connectPollInterval, setConnectPollInterval] = useState(300);

  const fetchStatus = async () => {
    try {
      const data = await apiClient.getClickHouseStatus();
      if (data?.connected) {
        setConnected(true);
        setLastPollAt(data.last_poll_at || null);
        setResourcesTracked(data.resources_tracked || 0);
        setPollInterval(data.poll_interval || 300);
        setOrganization(data.org_name || data.org_id || '');
      } else {
        setConnected(false);
      }
    } catch {
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleConnect = async () => {
    setError(null);
    setSuccessMsg(null);
    setSaving(true);
    try {
      await apiClient.connectClickHouse({
        key_id: keyId,
        key_secret: keySecret,
        org_id: connectOrg || undefined,
        poll_interval: connectPollInterval,
      });
      setSuccessMsg('ClickHouse connected successfully!');
      setKeyId('');
      setKeySecret('');
      await fetchStatus();
    } catch (err: any) {
      setError(err.response?.data || err.message || 'Failed to connect');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    setError(null);
    setSuccessMsg(null);
    try {
      await apiClient.disconnectClickHouse();
      setConnected(false);
      setSuccessMsg('ClickHouse disconnected.');
    } catch (err: any) {
      setError(err.response?.data || 'Failed to disconnect');
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const data = await apiClient.testClickHouse();
      if (data?.success) {
        setTestResult({ success: true, message: `Connected to ClickHouse Cloud (${data.service_count ?? 0} service${data.service_count !== 1 ? 's' : ''} accessible)` });
      } else {
        setTestResult({ success: false, message: data?.error || 'Connection test failed' });
      }
    } catch (err: any) {
      setTestResult({ success: false, message: err.response?.data || 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {successMsg && (
        <Alert className="border-green-500 bg-green-50 dark:bg-green-950/30">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700 dark:text-green-400">{successMsg}</AlertDescription>
        </Alert>
      )}
      {connected ? (
        <>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <img src={clickhouseIcon} alt="ClickHouse" className="w-7 h-7 object-contain" />
              <div>
                <CardTitle className="flex items-center gap-2">
                  ClickHouse
                  <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-normal">Connected</Badge>
                </CardTitle>
                <CardDescription>ClickHouse Cloud service lifecycle, scaling and IP access, backups, ClickPipes ingestion, error-spike detection, usage costs, and investigation automation</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              {organization && (
                <div>
                  <span className="text-muted-foreground">Organization</span>
                  <p className="font-medium">{organization}</p>
                </div>
              )}
              <div>
                <span className="text-muted-foreground">Resources Tracked</span>
                <p className="font-medium">{resourcesTracked.toLocaleString()} resource{resourcesTracked !== 1 ? 's' : ''}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Poll Interval</span>
                <p className="font-medium">{formatPollInterval(pollInterval)}</p>
              </div>
              {lastPollAt && (
                <div>
                  <span className="text-muted-foreground">Last Poll</span>
                  <p className="font-medium">{new Date(lastPollAt).toLocaleString()}</p>
                </div>
              )}
            </div>

            {testResult && (
              <Alert variant={testResult.success ? 'default' : 'destructive'} className={testResult.success ? 'border-green-500 bg-green-50 dark:bg-green-950/30' : ''}>
                {testResult.success ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4" />}
                <AlertDescription className={testResult.success ? 'text-green-700 dark:text-green-400' : ''}>{testResult.message}</AlertDescription>
              </Alert>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleTest} disabled={testing} className="gap-1">
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Test Connection
              </Button>
              <Button variant="outline" size="sm" onClick={handleDisconnect} className="gap-1">
                <Unplug className="w-3.5 h-3.5" /> Disconnect
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Available Workflow Blocks</CardTitle>
            <CardDescription>Use these ClickHouse actions and triggers in your workflows to automate cost control, incident response, ingestion recovery, and provisioning.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 mb-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Triggers (poll-based)</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'Service State Changed', desc: 'Triggers when a service changes state (running, idle, stopped, ...).', Icon: RefreshCw },
                  { name: 'Service Idle', desc: 'Triggers when a service scales to idle/stopped (cost).', Icon: Pause },
                  { name: 'Service Scaled', desc: 'Triggers when replicas or memory limits change.', Icon: Sliders },
                  { name: 'Backup Completed', desc: 'Triggers when a service backup completes.', Icon: Archive },
                  { name: 'Backup Failed', desc: 'Triggers when a service backup enters an error state.', Icon: AlertOctagon },
                  { name: 'Query Error Spike', desc: 'Triggers when failed queries/inserts spike past a threshold.', Icon: TrendingUp },
                  { name: 'ClickPipe Failed', desc: 'Triggers when an ingestion pipeline enters a failed state.', Icon: AlertTriangle },
                  { name: 'Version Changed', desc: 'Triggers when a service version changes after an upgrade.', Icon: ArrowUpCircle },
                  { name: 'Usage/Spend Threshold', desc: 'Triggers when month-to-date spend crosses a budget percent.', Icon: Gauge },
                  { name: 'Too Many Parts', desc: 'Triggers when a partition\u2019s part count exceeds a threshold.', Icon: Layers },
                  { name: 'High Query Concurrency', desc: 'Triggers when concurrent queries exceed a threshold.', Icon: Activity },
                ].map(block => (
                  <div key={block.name} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm min-w-0 truncate">{block.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'List Services', desc: 'List services with state, region, and version.', Icon: List },
                  { name: 'Get Service', desc: 'Fetch a service\u2019s state, scaling, and IP access list.', Icon: Info },
                  { name: 'Create Service', desc: 'Provision a service (gate behind Approval).', Icon: PlusCircle },
                  { name: 'Start Service', desc: 'Start or wake a stopped/idle service.', Icon: Play },
                  { name: 'Stop Service', desc: 'Stop a service to cut compute cost.', Icon: Pause },
                  { name: 'Update Autoscaling', desc: 'Adjust replica memory, replicas, idle scaling.', Icon: Sliders },
                  { name: 'Update IP Access List', desc: 'Add/remove CIDR entries (security).', Icon: Shield },
                  { name: 'List Backups', desc: 'List a service\u2019s backups and statuses.', Icon: Archive },
                  { name: 'Get Backup', desc: 'Fetch a backup\u2019s status, size, and timing.', Icon: Info },
                  { name: 'Update Backup Configuration', desc: 'Set backup period, retention, start time.', Icon: Settings },
                  { name: 'Restore Backup', desc: 'Restore into a new service (gate behind Approval).', Icon: RotateCcw },
                  { name: 'Delete Service', desc: 'Delete a stopped service (gate behind Approval).', Icon: Trash2 },
                  { name: 'List API Keys', desc: 'List org API keys (metadata only).', Icon: KeyRound },
                  { name: 'Get Service Metrics', desc: 'Fetch failed-query Prometheus counters.', Icon: Activity },
                  { name: 'Get Usage Costs', desc: 'Org spend report in ClickHouse Credits.', Icon: DollarSign },
                  { name: 'List ClickPipes', desc: 'List ingestion pipelines with state.', Icon: List },
                  { name: 'Get ClickPipe', desc: 'Fetch a ClickPipe\u2019s state and scaling.', Icon: Info },
                  { name: 'Start ClickPipe', desc: 'Resume a stopped/paused ingestion pipeline.', Icon: Play },
                  { name: 'Stop ClickPipe', desc: 'Pause an ingestion pipeline.', Icon: Pause },
                  { name: 'Resync ClickPipe', desc: 'Resync a CDC pipe from the source (Approval).', Icon: RefreshCw },
                  { name: 'Scale ClickPipe', desc: 'Update a ClickPipe\u2019s replica count.', Icon: Sliders },
                  { name: 'Investigate ClickHouse', desc: 'AI investigates services, backups, ClickPipes, metrics, costs.', Icon: Search },
                  { name: 'Get / Set / Clear Autoscaling Schedule', desc: 'Native business-hours scheduled autoscaling (cost).', Icon: CalendarClock },
                  { name: 'Get / Set / Clear Upgrade Window', desc: 'Control when version upgrades occur (maintenance).', Icon: CalendarX },
                  { name: 'Get / Update / Reset ClickHouse Setting', desc: 'Tune server/query settings, e.g. max_concurrent_queries.', Icon: Settings2 },
                  { name: 'Get / Upsert / Delete Query Endpoint', desc: 'Manage the query API endpoint (roles, origins).', Icon: Plug },
                  { name: 'List Members', desc: 'List org members for access review.', Icon: Users },
                  { name: 'Remove Member', desc: 'Offboard an org member (gate behind Approval).', Icon: UserMinus },
                  { name: 'List Roles', desc: 'List org roles for access review.', Icon: List },
                  { name: 'List Activity', desc: 'Org audit/activity log over a recent window.', Icon: History },
                ].map(block => (
                  <div key={block.name} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm min-w-0 truncate">{block.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Example Workflow</CardTitle>
            <CardDescription>Click the highlighted fields below to customize, then create the workflow.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground leading-loose">
              When a backup fails on the{' '}
              <input
                value={exampleService}
                onChange={e => setExampleService(e.target.value)}
                placeholder="service name"
                style={{ width: `${Math.max(exampleService.length || 12, 12)}ch` }}
                className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
              />{' '}ClickHouse service, investigate the failure with AI and post the findings to{' '}
              <input
                value={exampleChannel}
                onChange={e => setExampleChannel(e.target.value)}
                placeholder="#channel"
                style={{ width: `${Math.max(exampleChannel.length || 8, 8)}ch` }}
                className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
              />{' '}on Slack, then create a Jira ticket.
            </div>
            <Button
              size="sm"
              className="gap-1.5"
              disabled={!exampleService || !exampleChannel}
              onClick={() => navigate('/workflows/new', {
                state: {
                  suggestion: {
                    nl_prompt: `When a backup fails on the ${exampleService} ClickHouse service, investigate the failure with AI and post the findings to ${exampleChannel} on Slack, then create a Jira ticket.`,
                  },
                },
              })}
            >
              Create This Workflow <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </CardContent>
        </Card>
        </>
      ) : (
        <>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <img src={clickhouseIcon} alt="ClickHouse" className="w-7 h-7 object-contain" />
              <div>
                <CardTitle>ClickHouse</CardTitle>
                <CardDescription>Connect ClickHouse Cloud for service lifecycle (start/stop/scale), IP access, backups, ClickPipes ingestion, error-spike detection, usage costs, and investigation automation in workflows.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm">API Key</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">
                Kestrel authenticates to the ClickHouse Cloud API at <code className="bg-muted px-1 rounded">api.clickhouse.cloud</code> with an <strong className="text-foreground">API key ID + secret</strong> (HTTP Basic auth) to read and manage services, backups, and ClickPipes. Create one in the ClickHouse Cloud console (<code className="bg-muted px-1 rounded">console.clickhouse.cloud</code>): click your <strong className="text-foreground">organization</strong> name (bottom-left) to open the organization menu, choose <strong className="text-foreground">API keys → New API key</strong>, and assign the <strong className="text-foreground">Admin</strong> role (Developer keys are read-only and cannot start/stop/scale services). The key ID and secret are shown only once.
              </p>
              <Input
                id="clickhouse-key-id"
                value={keyId}
                onChange={e => setKeyId(e.target.value)}
                placeholder="Key ID"
                className="mt-1 font-mono"
              />
              <Input
                id="clickhouse-key-secret"
                type="password"
                value={keySecret}
                onChange={e => setKeySecret(e.target.value)}
                placeholder="Key secret"
                className="mt-2 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Stored encrypted and only used to authenticate requests to the ClickHouse Cloud API.
              </p>
            </div>
            <div>
              <Label htmlFor="clickhouse-org" className="text-sm">Organization ID (optional)</Label>
              <Input
                id="clickhouse-org"
                value={connectOrg}
                onChange={e => setConnectOrg(e.target.value)}
                placeholder="Auto-detected from the API key"
                className="mt-1 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">
                A ClickHouse Cloud API key belongs to one organization, so Kestrel auto-detects it. You can find it in the console under <strong className="text-foreground">Organization details</strong> if you want to set it explicitly.
              </p>
            </div>
            <div>
              <Label htmlFor="clickhouse-poll" className="text-sm">Poll Interval</Label>
              <select
                id="clickhouse-poll"
                value={connectPollInterval}
                onChange={e => setConnectPollInterval(Number(e.target.value))}
                className="mt-1 w-full sm:w-auto block rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {POLL_INTERVAL_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                ClickHouse Cloud has no control-plane webhooks, so Kestrel polls the Cloud API to detect service, backup, ClickPipe, error-spike, and spend events. Faster intervals detect events sooner; the minimum is 60 seconds.
              </p>
            </div>
            <Button onClick={handleConnect} disabled={saving || !keyId || !keySecret}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Connect ClickHouse
            </Button>
          </CardContent>
        </Card>
        </>
      )}
    </div>
  );
};

export default ClickHouseIntegration;
